/**
 * Entries View - Admin view to review attorney entries for a specific date.
 *
 * Uses the same EntriesTable component as the attorney view (DRY principle).
 * Differences from attorney view:
 * - No checkboxes (read-only)
 * - Click opens detail dialog instead of edit dialog
 * - No export/combine/delete actions
 */

import { useState, useMemo, useCallback } from "react";
import { CalendarIcon, ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/components/ui/command";
import { Calendar } from "@/shared/components/ui/calendar";
import { cn } from "@/shared/lib/utils";
import { useProfessionals, useUserTransactions, AdminTransaction } from "../hooks/useAdminData";
import { usePersistedState } from "../hooks/usePersistedState";
import AdminEntryDetailDialog, { FeedbackPayload } from "../components/AdminEntryDetailDialog";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import toast from "react-hot-toast";
import styles from "./AdminViews.module.css";

// Reuse the same EntriesTable component (DRY)
import EntriesTable, { Entry, EntryStatus, ColumnWidths } from "../../attorney/components/EntriesTable/EntriesTable";

/** Sort options for admin entries view */
type SortOption = "time" | "matter" | "status";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "time", label: "Time" },
  { value: "matter", label: "Matter" },
  { value: "status", label: "Status" },
];

function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Format ISO date string to short display format (e.g., "Jan 16") */
function formatDateForDisplay(isoDate: string): string {
  // Handle both "YYYY-MM-DD" and full ISO strings like "2026-01-16T00:00:00.000Z"
  // Extract just the date part before any "T" separator
  const datePart = isoDate.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day); // Creates date in local time
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Derive entry status from source table and exported flag.
 * - deleted_transactions → "deleted"
 * - transactions + exported=true → "exported"
 * - transactions + exported=false → "tracked"
 */
function deriveStatus(tx: AdminTransaction): EntryStatus {
  if (tx.source === "deleted_transactions") return "deleted";
  if (tx.exported) return "exported";
  return "tracked";
}

/** Transform AdminTransaction to Entry type for EntriesTable */
function toEntry(tx: AdminTransaction): Entry {
  return {
    id: tx.id,
    date: formatDateForDisplay(tx.date),
    matter: tx.matter_number || "",
    matter_id: tx.matter_id || undefined,
    matter_name: tx.matter_description || undefined,
    description: tx.description || "",
    task: tx.task_code || "",
    activity: tx.activity_code || undefined,
    document_name: tx.document_name || undefined,
    duration: tx.units,
    end_time: tx.end_time || undefined,
    application: tx.app_name,
    status: deriveStatus(tx),
  };
}

/**
 * Convert hour label like "9:00 AM" to 24-hour number for sorting.
 * "12:00 AM" = 0, "1:00 AM" = 1, ..., "12:00 PM" = 12, "1:00 PM" = 13, etc.
 */
function hourLabelTo24Hour(label: string): number {
  if (label === "Unknown") return 99; // Sort unknown to end
  const match = label.match(/^(\d+):00 (AM|PM)$/);
  if (!match) return 99;
  const hour = parseInt(match[1], 10);
  const isPM = match[2] === "PM";
  if (hour === 12) return isPM ? 12 : 0; // 12 PM = 12, 12 AM = 0
  return isPM ? hour + 12 : hour;
}

/** Group entries by hour, returning groups in chronological order */
function groupEntriesByHour(entries: Entry[]): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {};
  entries.forEach((entry) => {
    let hourLabel = "Unknown";
    if (entry.end_time) {
      // Parse time string directly (format: "HH:MM:SS" or "HH:MM")
      // Don't use new Date() - it causes timezone issues
      const parts = entry.end_time.split(":");
      if (parts.length >= 1) {
        const hours = parseInt(parts[0], 10);
        if (!isNaN(hours)) {
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
      }
    }
    if (!groups[hourLabel]) groups[hourLabel] = [];
    groups[hourLabel].push(entry);
  });

  // Sort keys chronologically and build ordered result
  const sortedKeys = Object.keys(groups).sort(
    (a, b) => hourLabelTo24Hour(a) - hourLabelTo24Hour(b)
  );
  const orderedGroups: Record<string, Entry[]> = {};
  sortedKeys.forEach((key) => {
    orderedGroups[key] = groups[key];
  });
  return orderedGroups;
}

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  date: 5,
  matter: 25,
  codes: 7,
  narrative: 55,
  units: 8,
};

// Date min width of 5% is enough for "Jan 16" format (~45px on most screens)
const COLUMN_MIN_WIDTHS: ColumnWidths = { date: 5, matter: 8, codes: 6, narrative: 10, units: 6 };
const COLUMN_ORDER: (keyof ColumnWidths)[] = ["date", "matter", "codes", "narrative", "units"];

/** Get today's date as YYYY-MM-DD string */
function getTodayString(): string {
  return formatDateForApi(new Date());
}

export default function EntriesView() {
  // Persisted filter state (survives navigation, refresh, browser close)
  const [selectedDateStr, setSelectedDateStr] = usePersistedState<string>(
    "admin:entries:date",
    getTodayString()
  );
  const [selectedProfId, setSelectedProfId] = usePersistedState<string | null>(
    "admin:entries:professional",
    null
  );

  // Derived Date object for calendar component
  const selectedDate = useMemo(() => {
    const [year, month, day] = selectedDateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [selectedDateStr]);

  // Helper to update date from Date object
  const setSelectedDate = (date: Date) => setSelectedDateStr(formatDateForApi(date));

  // Non-persisted UI state
  const [attorneySearch, setAttorneySearch] = useState("");
  const [attorneyOpen, setAttorneyOpen] = useState(false);
  const [sortBy, setSortBy] = usePersistedState<SortOption>(
    "admin:entries:sortBy",
    "time"
  );
  const [sortOpen, setSortOpen] = useState(false);

  // Dialog state
  const [selectedTx, setSelectedTx] = useState<AdminTransaction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);

  // API for saving feedback
  const { makeApiRequest } = useApi();

  // Column widths with resize support (same as TrackedView)
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(DEFAULT_COLUMN_WIDTHS);

  // Data fetching - only show professionals with tracked time
  const { professionals, loading: profLoading } = useProfessionals({ activeOnly: true });
  const { transactions, setTransactions, loading: txLoading } = useUserTransactions(
    selectedDateStr, // Already in YYYY-MM-DD format
    selectedProfId,
    { includeDeleted: true } // Include deleted transactions for admin view
  );

  // Transform transactions to Entry format and apply sorting
  const entries = useMemo(() => {
    // Sort based on selected option
    switch (sortBy) {
      case "matter":
        // Sort alphabetically, but put entries without matter at the bottom
        return [...transactions].map(toEntry).sort((a, b) => {
          const matterA = a.matter_name || a.matter || "";
          const matterB = b.matter_name || b.matter || "";
          // Empty matters go to the bottom
          if (!matterA && matterB) return 1;
          if (matterA && !matterB) return -1;
          if (!matterA && !matterB) return 0;
          return matterA.toLowerCase().localeCompare(matterB.toLowerCase());
        });
      case "status":
        // Order: exported (0) → tracked (1) → deleted (2)
        const statusOrder: Record<EntryStatus, number> = { exported: 0, tracked: 1, deleted: 2 };
        return [...transactions].map(toEntry).sort((a, b) => {
          const orderA = statusOrder[a.status || "tracked"];
          const orderB = statusOrder[b.status || "tracked"];
          return orderA - orderB;
        });
      case "time":
      default:
        // Sort by start_time ascending for chronological order through the day
        return [...transactions]
          .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
          .map(toEntry);
    }
  }, [transactions, sortBy]);

  // Group entries based on sort type
  const groupedEntries = useMemo(() => {
    if (entries.length === 0) return {};

    switch (sortBy) {
      case "time":
        return groupEntriesByHour(entries);

      case "matter":
        // Group by matter description, UNKNOWN entries are already at the bottom from sorting
        const matterGroups: Record<string, Entry[]> = {};
        entries.forEach((entry) => {
          const key = entry.matter_name || entry.matter || "UNKNOWN";
          if (!matterGroups[key]) matterGroups[key] = [];
          matterGroups[key].push(entry);
        });
        return matterGroups;

      case "status":
        // Group by status with custom labels
        const statusLabels: Record<EntryStatus, string> = {
          deleted: "Deleted Entries",
          tracked: "Non-Exported Entries",
          exported: "Exported Entries",
        };
        const statusGroups: Record<string, Entry[]> = {};
        entries.forEach((entry) => {
          const status = entry.status || "tracked";
          const key = statusLabels[status];
          if (!statusGroups[key]) statusGroups[key] = [];
          statusGroups[key].push(entry);
        });
        // Return in order: Exported → Non-Exported → Deleted
        const orderedGroups: Record<string, Entry[]> = {};
        ["Exported Entries", "Non-Exported Entries", "Deleted Entries"].forEach((label) => {
          if (statusGroups[label]) orderedGroups[label] = statusGroups[label];
        });
        return orderedGroups;

      default:
        return groupEntriesByHour(entries);
    }
  }, [entries, sortBy]);

  // Filter professionals by search
  const filteredProfs = useMemo(() => {
    if (!attorneySearch) return professionals.slice(0, 10);
    const term = attorneySearch.toLowerCase();
    return professionals.filter(p =>
      p.full_name.toLowerCase().includes(term) || p.email.toLowerCase().includes(term)
    ).slice(0, 10);
  }, [professionals, attorneySearch]);

  const selectedProfName = professionals.find(p => p.prof_id === selectedProfId)?.full_name;

  // Handle row click - find original AdminTransaction and open dialog
  const handleRowClick = (entry: Entry) => {
    const tx = transactions.find(t => t.id === entry.id);
    if (tx) {
      setSelectedTx(tx);
      setDialogOpen(true);
    }
  };

  // Navigation: compute current index and bounds based on SORTED entries (not raw transactions)
  // This ensures navigation respects the current sort/filter applied by the user
  const currentIndex = useMemo(() => {
    if (!selectedTx || !entries.length) return -1;
    return entries.findIndex(e => e.id === selectedTx.id);
  }, [selectedTx, entries]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < entries.length - 1 && currentIndex !== -1;

  // Handle navigation between entries - use sorted entries array for correct order
  const handleNavigate = useCallback((direction: "previous" | "next") => {
    const newIndex = direction === "previous" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < entries.length) {
      // Get the entry ID from the sorted entries array
      const entryId = entries[newIndex].id;
      // Find the corresponding AdminTransaction to set as selected
      const tx = transactions.find(t => t.id === entryId);
      if (tx) {
        setSelectedTx(tx);
      }
    }
  }, [currentIndex, entries, transactions]);

  // Handle saving feedback
  const handleSaveFeedback = useCallback(async (payload: FeedbackPayload) => {
    setIsSavingFeedback(true);
    try {
      const response = await makeApiRequest({
        endpoint: `/admin/transactions/${payload.entryId}/feedback`,
        method: "POST",
        body: {
          approved: payload.approved,
          needs_improvement: payload.needsImprovement,
          deducted: payload.deducted,
          attorney_feedback: payload.attorneyFeedback,
          hourglass_feedback: payload.hourglassFeedback,
        },
      });

      if (response.error) {
        // Entry no longer exists in transactions table (exported or deleted)
        if (response.error.toLowerCase().includes("not found")) {
          toast.error("This entry has been exported or deleted and can no longer be updated");
          throw new Error("Entry not found");
        }
        throw new Error(response.error);
      }

      // Update transactions state so navigation shows correct data
      setTransactions(prev =>
        prev.map(t =>
          t.id === payload.entryId
            ? {
                ...t,
                approved: payload.approved,
                needs_improvement: payload.needsImprovement,
                deducted: payload.deducted,
                attorney_feedback: payload.attorneyFeedback,
                hourglass_feedback: payload.hourglassFeedback,
              }
            : t
        )
      );

      logger.info("Feedback saved", { entryId: payload.entryId, approved: payload.approved, deducted: payload.deducted });
      toast.success("Feedback saved");
    } catch (error) {
      const errorMsg = "Failed to save feedback";
      logger.error(errorMsg, { error: error instanceof Error ? error.message : String(error) });
      toast.error(errorMsg);
      throw error; // Re-throw so dialog knows save failed
    } finally {
      setIsSavingFeedback(false);
    }
  }, [makeApiRequest, setTransactions]);

  // Column resize handler (same logic as TrackedView for full coupling)
  const handleCascadingResize = (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => {
    const currentIndex = COLUMN_ORDER.indexOf(resizingColumn);
    if (currentIndex === -1) return;
    const newWidths = { ...columnWidths };
    const currentWidth = columnWidths[resizingColumn];
    const minWidth = COLUMN_MIN_WIDTHS[resizingColumn];
    if (deltaPercentage > 0) {
      let availableSpace = 0;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length; i++) {
        const key = COLUMN_ORDER[i];
        availableSpace += Math.max(0, columnWidths[key] - COLUMN_MIN_WIDTHS[key]);
      }
      const actualExpansion = Math.min(deltaPercentage, availableSpace);
      if (actualExpansion <= 0) return;
      newWidths[resizingColumn] = currentWidth + actualExpansion;
      let remaining = actualExpansion;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length && remaining > 0.01; i++) {
        const key = COLUMN_ORDER[i];
        const canRemove = Math.max(0, columnWidths[key] - COLUMN_MIN_WIDTHS[key]);
        const toRemove = Math.min(remaining, canRemove);
        if (toRemove > 0.01) {
          newWidths[key] = columnWidths[key] - toRemove;
          remaining -= toRemove;
        }
      }
    } else {
      const maxShrinkage = currentWidth - minWidth;
      const actualShrinkage = Math.max(deltaPercentage, -maxShrinkage);
      if (actualShrinkage >= 0) return;
      newWidths[resizingColumn] = currentWidth + actualShrinkage;
      const spaceToGive = -actualShrinkage;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length; i++) {
        newWidths[COLUMN_ORDER[i]] = columnWidths[COLUMN_ORDER[i]] + spaceToGive;
        break;
      }
    }
    setColumnWidths(newWidths);
  };

  // Dummy handlers for checkbox props (admin view has no selection)
  const noop = () => {};
  const emptySet = new Set<number>();

  return (
    <div className={cn("flex flex-col gap-4 p-6 bg-background h-full", styles.adminViewWrapper)}>
      {/* Header row with filters - left aligned, order: Date, Professional, Sort */}
      <div className="flex items-center justify-start gap-3">
        {/* Date Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[160px] justify-start">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 space-y-2">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedDate(new Date())}>Today</Button>
                <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 1); setSelectedDate(d); }}>Yesterday</Button>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  if (d) {
                    // react-day-picker may return dates at UTC midnight, which in US timezones
                    // appears as the previous day. Extract UTC date parts and create local date.
                    const year = d.getUTCFullYear();
                    const month = d.getUTCMonth();
                    const day = d.getUTCDate();
                    const normalized = new Date(year, month, day, 12, 0, 0);
                    setSelectedDate(normalized);
                  }
                }}
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Professional Dropdown */}
        <Popover open={attorneyOpen} onOpenChange={setAttorneyOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[220px] justify-between">
              {selectedProfName || "Select attorney..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0">
            <Command>
              <CommandInput placeholder="Search..." value={attorneySearch} onValueChange={setAttorneySearch} />
              <CommandList>
                <CommandEmpty>{profLoading ? "Loading..." : "No attorneys found."}</CommandEmpty>
                <CommandGroup>
                  {filteredProfs.map(prof => (
                    <CommandItem
                      key={prof.prof_id}
                      value={`${prof.full_name} ${prof.email}`}
                      onSelect={() => { setSelectedProfId(prof.prof_id); setAttorneyOpen(false); setAttorneySearch(""); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", selectedProfId === prof.prof_id ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col">
                        <span>{prof.full_name}</span>
                        <span className="text-xs text-muted-foreground">{prof.email}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Sort Dropdown */}
        <Popover open={sortOpen} onOpenChange={setSortOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[120px] justify-between">
              {SORT_OPTIONS.find(o => o.value === sortBy)?.label}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[140px] p-0">
            <Command>
              <CommandList>
                <CommandGroup>
                  {SORT_OPTIONS.map(option => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => { setSortBy(option.value); setSortOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", sortBy === option.value ? "opacity-100" : "opacity-0")} />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Table - reusing EntriesTable component */}
      <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
        {txLoading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : !selectedProfId ? (
          <EntriesTable
            entries={[]}
            groupedEntries={{}}
            columnWidths={columnWidths}
            onResizeColumn={(key, w) => setColumnWidths((prev) => ({ ...prev, [key]: w }))}
            onCascadingResize={handleCascadingResize}
            checkedRows={emptySet}
            selectAllChecked={false}
            selectAllIndeterminate={false}
            onToggleSelectAll={noop}
            onToggleRow={noop}
            showCheckboxes={false}
            mode="entries"
            cellPadding={12}
          />
        ) : (
          <EntriesTable
            entries={entries}
            groupedEntries={groupedEntries}
            columnWidths={columnWidths}
            onResizeColumn={(key, w) => setColumnWidths((prev) => ({ ...prev, [key]: w }))}
            onCascadingResize={handleCascadingResize}
            checkedRows={emptySet}
            selectAllChecked={false}
            selectAllIndeterminate={false}
            onToggleSelectAll={noop}
            onToggleRow={noop}
            onRowClick={handleRowClick}
            showCheckboxes={false}
            mode="entries"
            cellPadding={12}
          />
        )}
      </div>

      {/* Detail Dialog */}
      <AdminEntryDetailDialog
        entry={selectedTx}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onNavigate={handleNavigate}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onSaveFeedback={handleSaveFeedback}
        isSaving={isSavingFeedback}
      />
    </div>
  );
}
